import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Building2,
  Wrench,
  Calendar,
  Users,
  QrCode,
  LogOut,
  Menu,
  X,
  ClipboardCheck,
  Briefcase,
  Bath,
  Warehouse,
  Key,
  Eye,
  EyeOff,
  Loader2,
  User,
  Package,
  Bell,
  BarChart3,
  MessageSquareText,
  Stethoscope,
  UserCog,
  ShieldCheck,
  ShieldAlert,
  Zap,
  HeartPulse,
  FileText,
  Thermometer,
  FlaskConical,
  Gauge,
  ChevronLeft,
  MonitorSmartphone,
  CarFront,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const Layout = () => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [flyout, setFlyout] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const closeTimer = useRef(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleChangePassword = async () => {
    if (!passwordData.currentPassword) {
      toast.error(T.errNeedCurrent);
      return;
    }
    if (!passwordData.newPassword || passwordData.newPassword.length < 4) {
      toast.error(T.errWeak);
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(T.errMismatch);
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`${API}/auth/change-password`, 
        { 
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(T.successPwd);
      setPasswordDialogOpen(false);
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || T.failPwd);
    } finally {
      setSubmitting(false);
    }
  };

  const openPasswordDialog = () => {
    setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setPasswordDialogOpen(true);
  };

  const isSterileRole = ['sterilization_dirty', 'sterilization_packaging', 'sterilization_storage'].includes(user?.role);

  const STERILE_THEMES = {
    sterilization_dirty:     { name: "Decontamination Area", hex: "#dc2626", soft: "bg-red-50 text-red-700 border-red-200", dot: "bg-red-600" },
    sterilization_packaging: { name: "Packaging Area",       hex: "#2563eb", soft: "bg-blue-50 text-blue-700 border-blue-200", dot: "bg-blue-600" },
    sterilization_storage:   { name: "Sterile Area",         hex: "#059669", soft: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-600" },
  };
  const deptTheme = STERILE_THEMES[user?.role] || null;
  const T = isSterileRole
    ? {
        brand: "Lucent Dental Center",
        brandSub: "Sterilization",
        logoAlt: "Lucent",
        changePwd: "Change password",
        logout: "Log out",
        pwdTitle: "Change Password",
        pwdDesc: "Enter your current and new password",
        pwdCurrent: "Current password *",
        pwdNew: "New password *",
        pwdConfirm: "Confirm new password *",
        pwdHint: "Must be at least 4 characters",
        pwdCurrentPh: "Enter current password",
        pwdNewPh: "Enter new password",
        pwdConfirmPh: "Re-enter new password",
        submit: "Change Password",
        cancel: "Cancel",
        errNeedCurrent: "Please enter your current password",
        errWeak: "New password must be at least 4 characters",
        errMismatch: "Passwords do not match",
        successPwd: "Password changed successfully",
        failPwd: "Failed to change password",
      }
    : {
        brand: "لوسنت لطب الأسنان",
        brandSub: "نظام إدارة المرافق والتشغيل",
        logoAlt: "لوسنت",
        changePwd: "تغيير كلمة المرور",
        logout: "تسجيل الخروج",
        pwdTitle: "تغيير كلمة المرور",
        pwdDesc: "أدخل كلمة المرور الحالية والجديدة",
        pwdCurrent: "كلمة المرور الحالية *",
        pwdNew: "كلمة المرور الجديدة *",
        pwdConfirm: "تأكيد كلمة المرور الجديدة *",
        pwdHint: "يجب أن تكون 4 أحرف على الأقل",
        pwdCurrentPh: "أدخل كلمة المرور الحالية",
        pwdNewPh: "أدخل كلمة المرور الجديدة",
        pwdConfirmPh: "أعد إدخال كلمة المرور الجديدة",
        submit: "تغيير كلمة المرور",
        cancel: "إلغاء",
        errNeedCurrent: "يرجى إدخال كلمة المرور الحالية",
        errWeak: "كلمة المرور الجديدة يجب أن تكون 4 أحرف على الأقل",
        errMismatch: "كلمة المرور الجديدة غير متطابقة",
        successPwd: "تم تغيير كلمة المرور بنجاح",
        failPwd: "فشل في تغيير كلمة المرور",
      };

  const deviceItems = user?.role === "admin"
    ? [{ path: "/biomed-devices", icon: Wrench, label: "الأجهزة الكهربائية والطبية", roles: ["admin"] }]
    : user?.is_biomed_officer
    ? [
        { path: "/biomed-devices?tab=biomed", icon: HeartPulse, label: "الأجهزة الطبية الحيوية", roles: [user.role] },
        { path: "/biomed-devices?tab=electrical", icon: MonitorSmartphone, label: "الأجهزة الإلكترونية", roles: [user.role] },
        { path: "/biomed-devices?tab=electric", icon: Zap, label: "الأجهزة الكهربائية", roles: [user.role] },
        { path: "/biomed-devices?tab=safety", icon: ShieldAlert, label: "أجهزة السلامة", roles: [user.role] },
        { path: "/biomed-devices?tab=hazmat", icon: FlaskConical, label: "المواد الكيميائية (Hazmat)", roles: [user.role] },
      ]
    : [{ path: "/biomed-devices", icon: Wrench, label: "الأجهزة الكهربائية والطبية", roles: ["maintenance_manager", "technician", "maintenance_technician"] }];

  const navStructure = [
    { type: "item", path: "/dashboard", icon: LayoutDashboard, label: "لوحة التحكم", roles: ["admin", "maintenance_manager", "it_manager", "medical_manager", "procurement_manager", "nurse"] },
    { type: "item", path: "/checkin", icon: ClipboardCheck, label: "تسجيل حضور المقاول", roles: ["data_entry"] },
    {
      type: "group", key: "lucent", label: "منشآت لوسنت", icon: Building2,
      children: [
        { path: "/clinics", icon: Building2, label: "العيادات", roles: ["admin", "maintenance_manager", "it_manager", "medical_manager", "nurse"] },
        { path: "/offices", icon: Briefcase, label: "المكاتب", roles: ["admin", "maintenance_manager", "it_manager", "nurse"] },
        { path: "/restrooms", icon: Bath, label: "دورات المياه", roles: ["admin", "maintenance_manager", "nurse"] },
        { path: "/facilities", icon: Warehouse, label: "المرافق", roles: ["admin", "maintenance_manager", "it_manager", "nurse"] },
      ],
    },
    {
      type: "group", key: "maintenance", label: "الصيانة والتشغيل", icon: Wrench,
      children: [
        { path: "/maintenance", icon: Wrench, label: "طلبات الصيانة", roles: ["admin", "nurse", "maintenance_manager", "it_manager", "medical_manager", "technician"] },
        { path: "/ppm-contracts", icon: FileText, label: "عقود الصيانة الدورية", roles: ["admin", "maintenance_manager", ...(user?.is_biomed_officer || user?.is_ppm_officer ? [user.role] : [])] },
        { path: "/checkin", icon: ClipboardCheck, label: "تسجيل حضور المقاول", roles: ["admin", ...(user?.is_biomed_officer ? [user.role] : [])] },
        { path: "/schedule", icon: Calendar, label: "الجدولة", roles: ["admin", "maintenance_manager"] },
        { path: "/alerts", icon: Bell, label: "التنبيهات الذكية", roles: ["admin", "maintenance_manager", "it_manager", "medical_manager", "procurement_manager"] },
        { path: "/advanced-dashboard", icon: BarChart3, label: "التحليلات والخريطة", roles: ["admin", "maintenance_manager"] },
        { path: "/reports", icon: LayoutDashboard, label: "التقارير", roles: ["admin", "maintenance_manager", "it_manager", "medical_manager"] },
      ],
    },
    {
      type: "group", key: "devices", label: "الأجهزة والتقييم الدوري", icon: HeartPulse,
      children: [
        ...deviceItems,
        { path: "/evaluations", icon: ClipboardCheck, label: "التقييم الدوري", roles: ["admin", "maintenance_manager", "technician", "maintenance_technician"] },
      ],
    },
    {
      type: "group", key: "sterilization", label: "التعقيم", icon: ShieldCheck,
      children: [
        { path: "/sterilization", icon: ShieldCheck, label: isSterileRole ? "Sterilization Tracking" : "تتبع التعقيم", roles: ["admin", "sterilization_dirty", "sterilization_packaging", "sterilization_storage"] },
        { path: "/autoclave", icon: Thermometer, label: isSterileRole ? "Autoclave Log" : "سجل الأوتوكلاف", roles: ["admin", "sterilization_dirty"] },
        { path: "/sterilization-kpis", icon: Gauge, label: "مؤشرات التعقيم KPIs", roles: ["admin"] },
        { path: "/instruments", icon: Wrench, label: "Instruments Inventory", roles: ["admin", "sterilization_storage"] },
      ],
    },
    {
      type: "group", key: "medical", label: "السجلات الطبية", icon: Stethoscope,
      children: [
        { path: "/medical-report-requests", icon: FileText, label: "طلبات التقارير الطبية", roles: ["admin", "admin_assistant"] },
        { path: "/treatment-plans", icon: Stethoscope, label: "السجلات الطبية", roles: ["admin"] },
        { path: "/doctors-hub", icon: UserCog, label: "إدارة الأطباء والأسعار", roles: ["admin"] },
      ],
    },
    { type: "item", path: "/valet-admin", icon: CarFront, label: "Valet Parking", roles: ["admin"] },
    { type: "item", path: "/feedback-management", icon: MessageSquareText, label: "الاقتراحات والشكاوى", roles: ["admin", "maintenance_manager", "it_manager", "medical_manager", "procurement_manager"] },
    { type: "item", path: "/users", icon: Users, label: "المستخدمين", roles: ["admin"] },
  ];

  const visibleNav = user?.role === "data_entry"
    ? navStructure.filter((e) => e.type === "item" && e.roles.includes("data_entry"))
    : navStructure
        .map((entry) => {
          if (entry.type === "item") return entry.roles.includes(user?.role) ? entry : null;
          const children = entry.children.filter((c) => c.roles.includes(user?.role));
          return children.length > 0 ? { ...entry, children } : null;
        })
        .filter(Boolean);

  // Sterile roles keep a simple flat menu (LTR English layout)
  const flatItems = isSterileRole
    ? navStructure.flatMap((e) => (e.type === "group" ? e.children : [e])).filter((c) => c.roles.includes(user?.role))
    : [];

  const isGroupActive = (g) => g.children.some((c) => location.pathname === c.path.split("?")[0]);

  const openFlyout = (g) => (e) => {
    clearTimeout(closeTimer.current);
    const r = e.currentTarget.getBoundingClientRect();
    setFlyout({
      key: g.key,
      top: Math.min(r.top, window.innerHeight - (g.children.length * 42 + 70)),
      right: window.innerWidth - r.left + 10,
    });
  };
  const scheduleFlyoutClose = () => {
    closeTimer.current = setTimeout(() => setFlyout(null), 180);
  };
  const flyoutGroup = flyout ? visibleNav.find((e) => e.type === "group" && e.key === flyout.key) : null;


  const getRoleName = (role) => {
    const roles = {
      admin: "مدير النظام",
      maintenance_manager: "مدير الصيانة",
      it_manager: "مدير تقنية المعلومات",
      medical_manager: "المدير الطبي",
      procurement_manager: "مدير المشتريات",
      technician: "فني صيانة",
      nurse: "ممرضة",
      sterilization_dirty: "Decontamination Area",
      sterilization_packaging: "Packaging Area",
      sterilization_storage: "Sterile Area",
      admin_assistant: "مساعد إداري"
    };
    return roles[role] || role;
  };

  return (
    <div className={`app-layout ${isSterileRole ? "sterile-layout" : ""}`} dir={isSterileRole ? "ltr" : "rtl"} style={isSterileRole ? { direction: 'ltr' } : undefined}>
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="sidebar-overlay lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`} style={deptTheme ? { borderTop: `5px solid ${deptTheme.hex}` } : undefined}>
        {/* Logo */}
        <div className="sidebar-logo">
          <img 
            src="/lucent-logo.png" 
            alt={T.logoAlt} 
            className="object-contain"
          />
          <h1 className={isSterileRole ? 'leading-tight' : ''}>{T.brand}</h1>
          {deptTheme ? (
            <span
              className="mt-1 inline-block text-[10px] font-bold px-2.5 py-0.5 rounded-full tracking-wide"
              style={{ backgroundColor: deptTheme.hex, color: "#fff" }}
              data-testid="dept-badge-sidebar"
            >
              {deptTheme.name}
            </span>
          ) : (
            <p className="text-xs text-white/60 font-medium">{T.brandSub}</p>
          )}
          <button 
            className="lg:hidden text-white/70 hover:text-white absolute top-2 end-2 transition-colors"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav flex-1">
          {isSterileRole
            ? flatItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.path.slice(1)}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </NavLink>
              ))
            : visibleNav.map((entry) =>
                entry.type === "item" ? (
                  <NavLink
                    key={entry.path}
                    to={entry.path}
                    className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`nav-${entry.path.slice(1)}`}
                  >
                    <entry.icon className="w-5 h-5" />
                    <span>{entry.label}</span>
                  </NavLink>
                ) : (
                  <div
                    key={entry.key}
                    className="sidebar-group"
                    onMouseEnter={openFlyout(entry)}
                    onMouseLeave={scheduleFlyoutClose}
                  >
                    <button
                      type="button"
                      className={`sidebar-item w-full ${isGroupActive(entry) ? "active" : ""}`}
                      onClick={(e) => {
                        if (window.innerWidth >= 1024) {
                          flyout?.key === entry.key ? setFlyout(null) : openFlyout(entry)(e);
                        } else {
                          setExpandedGroup(expandedGroup === entry.key ? null : entry.key);
                        }
                      }}
                      data-testid={`nav-group-${entry.key}`}
                    >
                      <entry.icon className="w-5 h-5" />
                      <span>{entry.label}</span>
                      <ChevronLeft className={`w-4 h-4 ms-auto opacity-60 transition-transform ${expandedGroup === entry.key ? "-rotate-90" : ""}`} />
                    </button>
                    {expandedGroup === entry.key && (
                      <div className="sidebar-subitems lg:hidden">
                        {entry.children.map((c) => (
                          <NavLink
                            key={c.path}
                            to={c.path}
                            className={({ isActive }) => `sidebar-item sub ${isActive ? "active" : ""}`}
                            onClick={() => setSidebarOpen(false)}
                            data-testid={`nav-${c.path.replace("/", "").replace("?", "-")}`}
                          >
                            <c.icon className="w-4 h-4" />
                            <span>{c.label}</span>
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                )
              )}
        </nav>

        {/* User Profile Section - Compact Design */}
        <div className="mt-auto border-t border-white/15 pt-3 pb-1 px-2">
          {/* User Info Row with Actions */}
          <div className="flex items-center gap-2.5">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: deptTheme ? deptTheme.hex : "rgba(255,255,255,0.15)" }}
            >
              <User className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white truncate leading-tight">{user?.full_name}</p>
              <p className="text-[10px] text-white/50 leading-tight">{getRoleName(user?.role)}</p>
            </div>
            {/* Action Icons */}
            <div className="flex items-center gap-1">
              <button
                onClick={openPasswordDialog}
                className="p-1.5 text-white/60 hover:text-white hover:bg-white/10 rounded-md transition-colors"
                data-testid="change-own-password-btn"
                title={T.changePwd}
              >
                <Key className="w-4 h-4" />
              </button>
              <button
                onClick={handleLogout}
                className="p-1.5 text-red-300 hover:text-red-200 hover:bg-red-500/20 rounded-md transition-colors"
                data-testid="logout-btn"
                title={T.logout}
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Desktop flyout submenu */}
      {flyoutGroup &&
        createPortal(
          <div
            className="sidebar-flyout"
            dir="rtl"
            style={{ top: Math.max(flyout.top, 12), right: flyout.right }}
            onMouseEnter={() => clearTimeout(closeTimer.current)}
            onMouseLeave={scheduleFlyoutClose}
            data-testid={`flyout-${flyoutGroup.key}`}
          >
            <p className="flyout-title">{flyoutGroup.label}</p>
            {flyoutGroup.children.map((c) => (
              <NavLink
                key={c.path}
                to={c.path}
                className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}
                onClick={() => setFlyout(null)}
                data-testid={`flyout-nav-${c.path.replace("/", "").replace("?", "-")}`}
              >
                <c.icon className="w-4 h-4" />
                <span>{c.label}</span>
              </NavLink>
            ))}
          </div>,
          document.body
        )}

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="sm:max-w-md" dir={isSterileRole ? "ltr" : "rtl"}>
          <DialogHeader>
            <DialogTitle className="text-[#655dc6] flex items-center gap-2">
              <Key className="w-5 h-5" />
              {T.pwdTitle}
            </DialogTitle>
            <DialogDescription>
              {T.pwdDesc}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{T.pwdCurrent}</Label>
              <div className="relative">
                <Input
                  type={showCurrentPassword ? "text" : "password"}
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  placeholder={T.pwdCurrentPh}
                  className={`${isSterileRole ? 'text-left' : 'text-right'} pl-10`}
                  dir="ltr"
                  autoComplete="new-password"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{T.pwdNew}</Label>
              <div className="relative">
                <Input
                  type={showNewPassword ? "text" : "password"}
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  placeholder={T.pwdNewPh}
                  className={`${isSterileRole ? 'text-left' : 'text-right'} pl-10`}
                  dir="ltr"
                  autoComplete="new-password"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-slate-400">{T.pwdHint}</p>
            </div>

            <div className="space-y-2">
              <Label>{T.pwdConfirm}</Label>
              <Input
                type={showNewPassword ? "text" : "password"}
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                placeholder={T.pwdConfirmPh}
                className={isSterileRole ? 'text-left' : 'text-right'}
                dir="ltr"
                autoComplete="new-password"
                data-testid="confirm-password-input"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleChangePassword}
                className="flex-1 bg-[#655dc6] hover:bg-[#5449a8] text-white"
                disabled={submitting}
                data-testid="submit-change-password-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : T.submit}
              </Button>
              <Button
                variant="outline"
                onClick={() => setPasswordDialogOpen(false)}
                className="flex-1"
              >
                {T.cancel}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <main className="main-content">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between mb-6 bg-white p-4 rounded-xl border border-gray-200">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-gray-50 rounded-lg transition-colors"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6 text-[#655dc6]" />
          </button>
          <div className="flex items-center gap-2">
            <img 
              src="/lucent-logo.png" 
              alt={T.logoAlt} 
              className="w-10 h-10 object-contain"
            />
            <span className="font-bold text-[#0A0A0A]">{T.brand}</span>
          </div>
          <div className="w-10" />
        </div>

        {/* Department color banner for sterilization accounts */}
        {deptTheme && (
          <div className="mb-4" data-testid="dept-banner">
            <div className="h-1.5 rounded-full mb-3" style={{ background: `linear-gradient(90deg, ${deptTheme.hex}, ${deptTheme.hex}66)` }} />
            <div className={`inline-flex items-center gap-2 border rounded-full px-3 py-1 text-xs font-bold ${deptTheme.soft}`}>
              <span className={`w-2 h-2 rounded-full ${deptTheme.dot} animate-pulse`} />
              {deptTheme.name}{user?.full_name && user.full_name !== deptTheme.name ? ` — ${user.full_name}` : ""}
            </div>
          </div>
        )}

        {/* Page Content */}
        <div className="page-transition">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
